import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { CalendarCheck, Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import CalendlyButton from "@/components/CalendlyButton";

export default function Nav() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const links = [
    { to: "/", label: t("nav.links.home") },
    { to: "/about", label: t("nav.links.about") },
    { to: "/services", label: t("nav.links.services") },
    { to: "/tools", label: t("tools.nav") },
    { to: "/insights", label: t("nav.links.insights") },
    { to: "/nexus", label: "Nexus", accent: true },
    { to: "/faq", label: t("nav.links.faq") },
    { to: "/contact", label: t("nav.links.contact") },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || open ? "glass" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1480px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="brandmark group min-w-0 shrink" aria-label="Corpora Consultants — home">
          <img
            src="/brand/corpora-wordmark.png?v=4"
            alt="Corpora Consultants"
            className="brandmark-logo"
            width="915"
            height="106"
          />
        </Link>

        <nav className="hidden xl:flex items-center gap-1">
          {links.map((l, i) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${i}`}
              className={({ isActive }) =>
                `relative px-2.5 py-2 font-mono text-[11px] uppercase tracking-[0.16em] transition-colors duration-300 ${
                  l.accent
                    ? isActive
                      ? "text-[var(--pewter)]"
                      : "text-[var(--pewter)]/85 hover:text-[var(--pewter)]"
                    : isActive
                    ? "text-[var(--pewter)]"
                    : "text-[var(--bone-2)] hover:text-[var(--bone)]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <span className="mx-2 w-px h-4 bg-[var(--line-2)]" />
          <LanguageSwitcher />
          <CalendlyButton
            data-testid="nav-cta"
            className={`ms-3 !py-2.5 !px-5 ${scrolled ? "btn-primary" : "btn-secondary"}`}
          >
            {t("tools.book")}
          </CalendlyButton>
        </nav>

        <div className="xl:hidden flex items-center gap-2">
          <CalendlyButton
            data-testid="nav-mobile-sticky-cta"
            className={`btn-primary whitespace-nowrap !gap-2 !py-2 !px-3 !text-[10px] !tracking-[0.14em] transition-opacity duration-300 ${scrolled ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            aria-hidden={!scrolled}
            aria-label={t("tools.book")}
            tabIndex={scrolled ? 0 : -1}
          >
            <CalendarCheck size={14} />
            <span className="hidden sm:inline">{t("tools.book")}</span>
          </CalendlyButton>
          <LanguageSwitcher />
          <button
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            className="p-2 text-[var(--bone)]"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden border-t border-[var(--line)] px-6 py-8 flex flex-col gap-5">
          {links.map((l, i) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-mobile-${i}`}
              className="display text-3xl text-[var(--bone)] flex items-baseline gap-3"
            >
              <span className="font-mono text-[10px] tracking-[0.24em] text-[var(--mute-2)]">
                {String(i + 1).padStart(2, "0")}
              </span>
              {l.label}
            </NavLink>
          ))}
          <CalendlyButton className="btn-primary self-start mt-2" data-testid="nav-mobile-cta">
            {t("tools.book")}
          </CalendlyButton>
        </div>
      )}
    </header>
  );
}
